/**
 * Account deletion request helpers for the public /delete-account page.
 * Mirrors backend PR #167 (services/accountDeletionService.js):
 *   POST {backendUrl}/account/deletionRequest, JSON, no login
 *   body {email?, discord_name?, club_name?, reason?, website}  (unknown keys rejected, <= 4 KB)
 *   201 {ok, request_id} | 400 {reason:'invalid_input', field} | 429 {reason:'rate_limited'} + Retry-After
 *   500 {reason:'server_error'}
 */

// Termly data subject access request form, linked from /privacy section 16.
export const TERMLY_DATA_REQUEST_URL =
  'https://app.termly.io/dsar/fcd7dca9-9b79-496c-9370-103a2bd03b61';

// Contact address for privacy requests, from /privacy sections 10, 12 and 15.
export const PRIVACY_CONTACT_EMAIL = 'kote@squigglygames.com';

// Same limits as the backend.
export const FIELD_LIMITS = {
  email: 254,
  discordName: 32,
  clubName: 100,
  reason: 1000,
};
export const MAX_BODY_BYTES = 4096;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 2-32 of letters, digits, _ and ., optionally the legacy #1234 tag
const DISCORD_RE = /^[A-Za-z0-9_.]{2,32}(#\d{4})?$/;
// control characters other than tab / newline / carriage return
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const LINE_BREAK_RE = /[\r\n\t]/;

export const ERROR_MESSAGES = {
  identifier: 'Enter your account email or your Discord name.',
  email: 'Enter a valid email address.',
  discordName:
    'Enter your Discord username: 2–32 letters, numbers, dots or underscores (or the old Name#1234).',
  clubName: 'Keep the club name to one line of up to 100 characters.',
  reason: 'Keep the reason under 1000 characters.',
  body: 'Your request is too long. Shorten the reason and try again.',
};

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

export function normalizeDeletionRequest(values = {}) {
  return {
    email: clean(values.email),
    discordName: clean(values.discordName).replace(/^@/, ''),
    clubName: clean(values.clubName),
    reason: clean(values.reason),
  };
}

/** The exact JSON body: only the allowed keys, empty optional ones omitted, website always ''. */
export function buildDeletionPayload(values = {}) {
  const v = normalizeDeletionRequest(values);
  const payload = {};
  if (v.email) payload.email = v.email;
  if (v.discordName) payload.discord_name = v.discordName;
  if (v.clubName) payload.club_name = v.clubName;
  if (v.reason) payload.reason = v.reason;
  payload.website = '';
  return payload;
}

// UTF-8 byte length without TextEncoder (not in every test/browser environment).
const byteLength = (text) => encodeURIComponent(text).replace(/%[0-9A-F]{2}/gi, '_').length;

/** Returns { field: message } for every invalid form field; empty object when valid. */
export function validateDeletionRequest(values = {}) {
  const v = normalizeDeletionRequest(values);
  const errors = {};

  if (!v.email && !v.discordName) {
    errors.email = ERROR_MESSAGES.identifier;
    errors.discordName = ERROR_MESSAGES.identifier;
  }
  if (v.email && (v.email.length > FIELD_LIMITS.email || !EMAIL_RE.test(v.email))) {
    errors.email = ERROR_MESSAGES.email;
  }
  if (v.discordName && !DISCORD_RE.test(v.discordName)) {
    errors.discordName = ERROR_MESSAGES.discordName;
  }
  if (
    v.clubName &&
    (v.clubName.length > FIELD_LIMITS.clubName ||
    CONTROL_RE.test(v.clubName) ||
    LINE_BREAK_RE.test(v.clubName))
  ) {
    errors.clubName = ERROR_MESSAGES.clubName;
  }
  if (v.reason && (v.reason.length > FIELD_LIMITS.reason || CONTROL_RE.test(v.reason))) {
    errors.reason = ERROR_MESSAGES.reason;
  }
  if (!Object.keys(errors).length && byteLength(JSON.stringify(buildDeletionPayload(values))) > MAX_BODY_BYTES) {
    errors.reason = ERROR_MESSAGES.body;
  }
  return errors;
}

// Backend field names -> form fields.
const FIELD_MAP = {
  email: ['email'],
  discord_name: ['discordName'],
  club_name: ['clubName'],
  reason: ['reason'],
  email_or_discord_name: ['email', 'discordName'],
  body: ['reason'],
};

export class DeletionRequestError extends Error {
  constructor(kind, { status, field, fields, retryAfter } = {}) {
    super(`Deletion request failed: ${kind}`);
    this.kind = kind; // invalid_input | rate_limited | server_error | unavailable | network
    this.status = status;
    this.field = field;
    this.fields = fields || [];
    this.retryAfter = retryAfter;
  }
}

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

/** Retry-After in seconds (number or HTTP date), or null. */
export function parseRetryAfter(value, now = Date.now()) {
  if (value === null || value === undefined || value === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const date = Date.parse(value);
  if (Number.isFinite(date)) return Math.max(0, Math.ceil((date - now) / 1000));
  return null;
}

/**
 * POST a deletion request. Resolves { requestId } on 201, otherwise rejects with
 * a DeletionRequestError whose `kind` the form turns into a message.
 */
export async function submitDeletionRequest(
  endpoint,
  values,
  fetchImpl = (...args) => fetch(...args)
) {
  let response;
  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildDeletionPayload(values)),
    });
  } catch (e) {
    throw new DeletionRequestError('network');
  }
  const { status } = response;
  const data = await readJson(response);

  if (status === 201 || (response.ok && data && data.ok)) {
    return { requestId: (data && data.request_id) || null };
  }
  if (status === 400 && data && data.reason === 'invalid_input') {
    const fields = FIELD_MAP[data.field] || [];
    throw new DeletionRequestError('invalid_input', { status, field: data.field, fields });
  }
  if (status === 429) {
    const header = response.headers && response.headers.get ? response.headers.get('Retry-After') : null;
    throw new DeletionRequestError('rate_limited', { status, retryAfter: parseRetryAfter(header) });
  }
  if (status >= 500) {
    throw new DeletionRequestError('server_error', { status });
  }
  // 404 (route not live yet) and anything else unexpected
  throw new DeletionRequestError('unavailable', { status });
}

export function retryAfterText(seconds) {
  if (!seconds) return 'later';
  if (seconds < 60) return `in ${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `in about ${minutes} minute${minutes === 1 ? '' : 's'}`;
}
